/**
 * CompanyContentViewScreen - Detail view for company content entries
 * Mirrors the AcademyContentViewScreen pattern: fetch by contentId, render with
 * ExploreContentViewRenderer for entries or ExploreContentViewCategoryRenderer for categories.
 * Created by claude on 09.03.26
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { Container } from 'reactstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import { ExploreContentBuilder, KinasticContent } from '../../Model/Explore/ExploreContentBuilder';
import { ExploreContentViewRenderer } from '../../Components/EntryView/ExploreContentViewRenderer';
import { ExploreContentViewCategoryRenderer } from '../Academy/View/CategoryView/ExploreContentViewCategoryRenderer';
import { RelatedEntriesView } from '../../Components/RelatedEntriesView';
import { ExploreCategory } from '../../Model/Explore/ExploreCategory';
import { isCompanyRootCategory, canViewerAccessCompany } from '../../Model/Explore/companyCategory';
import { useRootStore } from '../../Store/useRootStore';

export const CompanyContentViewScreen: React.FC = observer(() => {
  const { analytics, gymCustomer } = useRootStore();
  const navigate = useNavigate();

  const [content, setContent] = useState<KinasticContent | undefined>();
  const [lastError, setLastError] = useState<AxiosError | undefined>();
  const [loading, setLoading] = useState(true);

  const { contentId } = useParams<{ contentId: string }>();

  useEffect(() => {
    setContent(undefined);
    setLastError(undefined);
    if (contentId) {
      setLoading(true);
      ExploreContentBuilder.findOne(contentId)
        .then((res) => setContent(res))
        .catch((e) => setLastError(e))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [contentId]);

  useEffect(() => {
    if (content) {
      const type = content.type === 'recipe' ? 'recipe' : content.type;
      analytics.logSelectContent(type, content.id);
    }
  }, [analytics, content]);

  // EPIC-0009: when the resolved content is a company-private root category,
  // redirect to the `/company` index route (for the viewer's own gym) or to `/`
  // (for cross-gym requests). The /company index already renders the fancy
  // company view from the viewer's own gymCustomer; we don't need to render it
  // inline at the /company/:id URL.
  //
  // Race-condition guard: gymCustomer.customer is fetched asynchronously after
  // auth resolves; the content fetch usually wins the race. If redirectTarget
  // were computed while customer was still undefined, the same-gym viewer would
  // be wrongly bounced to '/'. Gate on `customer !== undefined`.
  const categoryContent =
    content && 'contentType' in content && content.contentType === 'category' && 'identifier' in content
      ? (content as ExploreCategory)
      : undefined;
  const viewerGymId = gymCustomer.customer?.gymId;
  const isCompanyRoot = isCompanyRootCategory(categoryContent);
  const customerLoaded = gymCustomer.customer !== undefined;
  const redirectTarget = isCompanyRoot && customerLoaded
    ? canViewerAccessCompany(categoryContent, viewerGymId)
      ? '/company'
      : '/'
    : null;

  useEffect(() => {
    if (redirectTarget) {
      navigate(redirectTarget, { replace: true });
    }
  }, [redirectTarget, navigate]);

  if (redirectTarget) {
    return null;
  }

  // Hold rendering on a company-root URL until customer is known so we don't
  // flash the regular category view before the redirect resolves.
  if (isCompanyRoot && !customerLoaded) {
    return null;
  }

  if (content) {
    if (categoryContent) {
      return (
        <Container>
          <ExploreContentViewCategoryRenderer category={categoryContent} linkPrefix="/company/" />
        </Container>
      );
    }
    const exploreEntry = 'backgroundImage' in content ? content : undefined;
    return (
      <React.Fragment>
        <ExploreContentViewRenderer entry={content} />
        {exploreEntry && <RelatedEntriesView entry={exploreEntry} linkPrefix="/company/" />}
      </React.Fragment>
    );
  }

  if (loading) {
    return <Container style={{ padding: '48px', textAlign: 'center' }}>Loading...</Container>;
  }

  return <Container>{lastError && <div>Content not found</div>}</Container>;
});
